import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { Card, CardHeader, CardTitle, CardContent, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { SearchableSelect } from '../../components/ui/searchable-select';
import { TimePicker } from '../../components/ui/time-picker';
import { useToast } from '../../components/ui/use-toast';
import { useT } from '../../context/LanguageContext';
import {
  ArrowLeft, Plus, Trash2, Clock, Edit, Search, Layers, UtensilsCrossed, PlusCircle, Combine, RefreshCw,
  MapPin, CalendarDays, Check, ImagePlus, X, Globe,
} from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../../components/ui/dialog';
import {
  createMenu,
  updateMenu,
  getMenu,
  getAllDishes,
} from '../../services/menuService';
import { mastersService, dishMastersService } from '../../services/mastersService';
import { locationsService } from '../../services/locationsService';
import { confirm } from '../../components/ui/confirm-dialog';
import { useAppSettings } from '../../context/AppSettingsContext';

/**
 * Menu builder (Create / Edit).
 *
 * Layout: two columns.
 *   Left  — Menu Details + Sections list (compact; one click picks the active section).
 *   Right — Active section panel: type chips (Dish / Beverage / Add-on / Combo) +
 *           category dropdown + search → dishes filtered live, each with a
 *           checkbox, plus a chip-list of dishes already in the section.
 *
 * Save shape: one POST/PUT carrying the full menu — name, description, is_active,
 * and sections[] each with items[]. The MenuController rebuilds the structure
 * atomically.
 */

const TYPE_CHIPS = [
  { value: 'all',      label: 'All',      Icon: Layers },
  { value: 'dish',     label: 'Dish',     Icon: UtensilsCrossed },
  { value: 'addon',    label: 'Add-on',   Icon: PlusCircle },
  { value: 'combo',    label: 'Combo',    Icon: Combine },
];

// Weekly operating hours are stored Monday=0 … Sunday=6 (date.weekday()).
const HOURS_DAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

// "HH:MM" (24h) → "h:MM AM/PM" (12h). Returns the input unchanged if unparsable.
function _to12h(t) {
  const [h, m] = String(t).split(':').map(Number);
  if (Number.isNaN(h) || Number.isNaN(m)) return t;
  const period = h < 12 ? 'AM' : 'PM';
  const hh = h % 12 === 0 ? 12 : h % 12;
  return `${hh}:${String(m).padStart(2, '0')} ${period}`;
}

function _fmtDayHours(d) {
  if (!d || d.closed) return 'Closed';
  if (!d.open || !d.close) return '—';
  if (d.open === '00:00' && (d.close === '23:59' || d.close === '24:00')) return 'Open 24h';
  return `${_to12h(d.open)} – ${_to12h(d.close)}`;
}

// Compact label for a location's hours: today's window, with the full week in
// the tooltip. Returns null when no hours are configured.
function locationHours(operatingHours) {
  if (!Array.isArray(operatingHours) || operatingHours.length === 0) return null;
  const todayIdx = (new Date().getDay() + 6) % 7; // JS Sun=0 → Mon=0…Sun=6
  return {
    label: _fmtDayHours(operatingHours[todayIdx]),
    title: HOURS_DAYS.map((dn, i) => `${dn}: ${_fmtDayHours(operatingHours[i])}`).join('\n'),
  };
}

// Compact square icon button used beside the section picker — mirrors the
// +/refresh affordance the dish-form masters use (see AddEditDish.js).
function IconBtn({ icon: Icon, onClick, title, tone = 'primary', disabled = false }) {
  const palette = disabled
    ? 'border-slate-200 bg-slate-100 text-slate-300 cursor-not-allowed'
    : tone === 'subdued'
      ? 'border-slate-200 bg-white text-slate-500 hover:bg-slate-50 hover:text-slate-700 hover:border-slate-300'
      : 'border-primary/30 bg-accent text-accent-foreground hover:bg-accent/70 hover:border-primary/50';
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      title={title}
      aria-label={title}
      className={`inline-flex items-center justify-center h-7 w-7 rounded-md border transition-colors ${palette}`}
    >
      <Icon size={14} />
    </button>
  );
}

export default function MenuForm({ restaurantId: propRestaurantId }) {
  const navigate = useNavigate();
  const { menuId, restaurantId: urlRestaurantId, locationId } = useParams();
  const restaurantId = propRestaurantId || urlRestaurantId;
  // Every back/cancel/after-save path returns to this location's menu list.
  const menusBase = `/restaurant/${restaurantId}/locations/${locationId}/menus`;
  const { toast } = useToast();
  // Only the kiosk switch below goes through this so far; the rest of this
  // screen is still hardcoded English and is its own piece of work.
  const { t: tr } = useT();
  const { formatCurrency } = useAppSettings();
  const isEditing = Boolean(menuId);

  // Menu-level state
  const [menuName, setMenuName] = useState('');
  const [menuDescription, setMenuDescription] = useState('');
  const [isActive, setIsActive] = useState(true);
  // Service style: à la carte (per-dish ordering) or buffet (spread).
  const [menuType, setMenuType] = useState('ALACARTE');
  // Printed on the public marketing website. On by default — the site published
  // every active menu before this switch existed, so it is the opt-out.
  const [showOnWebsite, setShowOnWebsite] = useState(true);
  /* Whether a self-order kiosk offers this card. On by default, which is
     what a panel did before the switch existed — the tick is the opt-OUT. */
  const [showOnKiosk, setShowOnKiosk] = useState(true);
  // Which categories the website prints, held by section NAME — a section
  // typed into the builder has no id until it is saved, and the name is what
  // both this form and the API can talk about either side of that. Starts
  // empty on a new menu on purpose: the site never publishes a category
  // nobody ticked.
  const [websiteSections, setWebsiteSections] = useState([]);
  const [websiteShowPrices, setWebsiteShowPrices] = useState(true);
  // Multi-location: the branches this menu is served at. The location the
  // builder was opened under (locationId) is the primary and stays selected.
  const primaryLocationId = locationId ? Number(locationId) : null;
  const [locations, setLocations] = useState([]);
  const [selectedLocationIds, setSelectedLocationIds] = useState(
    primaryLocationId ? [primaryLocationId] : [],
  );
  // Menu-level daily availability window. `followLocationHours` makes the menu
  // run for each branch's full operating hours instead of a fixed clock window.
  const [activeFrom, setActiveFrom] = useState('');
  const [activeUntil, setActiveUntil] = useState('');
  const [followLocationHours, setFollowLocationHours] = useState(false);
  const [sections, setSections] = useState([]);   // [{ name, description, available_from, available_until, items: [...] }]
  const [activeSectionIdx, setActiveSectionIdx] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [showSectionDialog, setShowSectionDialog] = useState(false);
  const [editingSectionIndex, setEditingSectionIndex] = useState(null);
  const [sectionName, setSectionName] = useState('');
  const [sectionDescription, setSectionDescription] = useState('');
  const [sectionFromTime, setSectionFromTime] = useState('');
  const [sectionUntilTime, setSectionUntilTime] = useState('');
  const [sectionFollowHours, setSectionFollowHours] = useState(false);
  // Section cover photo — shown on the customer menu's category tiles. The
  // image lives on the section MASTER (shared wherever the section is used);
  // picking a file here uploads it to the master when the dialog is saved.
  const [sectionImageFile, setSectionImageFile] = useState(null);
  const [sectionImagePreview, setSectionImagePreview] = useState(null);
  const [sectionRemoveImage, setSectionRemoveImage] = useState(false);
  const sectionImageInputRef = useRef(null);
  // Inline "add a new section master" dialog (the + beside the picker).
  const [showSectionMasterDialog, setShowSectionMasterDialog] = useState(false);
  const [newMasterName, setNewMasterName] = useState('');
  const [newMasterDescription, setNewMasterDescription] = useState('');
  const [newMasterImageFile, setNewMasterImageFile] = useState(null);
  const [newMasterImagePreview, setNewMasterImagePreview] = useState(null);
  const newMasterImageInputRef = useRef(null);
  const [savingMaster, setSavingMaster] = useState(false);

  // Picker state
  const [pickerType, setPickerType] = useState('all');
  const [pickerCategory, setPickerCategory] = useState('all');
  const [pickerSubCategory, setPickerSubCategory] = useState('all');
  const [pickerSearch, setPickerSearch] = useState('');
  // EVERY dish (all types) fetched ONCE via getAllDishes; the chips/category/
  // search below filter this list client-side — no re-fetch per filter change.
  const [allDishes, setAllDishes] = useState([]);
  const [pickerLoading, setPickerLoading] = useState(false);
  // Bumped by the SECTION CONTENT refresh button to force a re-fetch of the
  // dish/beverage/add-on picker (e.g. after creating a new dish in another tab).
  const [pickerNonce, setPickerNonce] = useState(0);
  // Category / sub-category filter options are DERIVED from the dishes returned
  // by getAllDishes (each dish carries its category + sub-category) — no extra
  // category/ or sub_category/ master-data calls. Sub-categories inherit their
  // parent category_id from the dish they appear on, so the parent→child
  // dropdown link still works.
  const categories = useMemo(() => {
    const m = new Map();
    allDishes.forEach((d) => { if (d.category?.id) m.set(d.category.id, d.category.name); });
    return Array.from(m, ([id, name]) => ({ id, name }))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [allDishes]);
  const subCategories = useMemo(() => {
    const m = new Map();
    allDishes.forEach((d) => {
      if (d.sub_category?.id) {
        m.set(d.sub_category.id, {
          id: d.sub_category.id,
          name: d.sub_category.name,
          category_id: d.category?.id ?? null,
        });
      }
    });
    return Array.from(m.values()).sort((a, b) => a.name.localeCompare(b.name));
  }, [allDishes]);
  const [masterSections, setMasterSections] = useState([]);
  const [masterSectionsLoading, setMasterSectionsLoading] = useState(false);

  const loadMasterSections = useCallback(async () => {
    setMasterSectionsLoading(true);
    try {
      const rows = await mastersService.list('menuSections');
      setMasterSections(Array.isArray(rows) ? rows : []);
    } catch (error) {
      console.error('Error loading master menu sections:', error);
      setMasterSections([]);
    } finally {
      setMasterSectionsLoading(false);
    }
  }, []);

  const loadLocations = useCallback(async () => {
    if (!restaurantId) return;
    try {
      const rows = await locationsService.list(restaurantId);
      setLocations(Array.isArray(rows) ? rows : []);
    } catch (error) {
      console.error('Error loading locations:', error);
      setLocations([]);
    }
  }, [restaurantId]);

  const loadMenu = useCallback(async () => {
    setLoading(true);
    try {
      const data = await getMenu(restaurantId, menuId);
      setMenuName(data.name || '');
      setMenuDescription(data.description || '');
      setIsActive(data.is_active !== false);
      setMenuType(data.menu_type === 'BUFFET' ? 'BUFFET' : 'ALACARTE');
      setShowOnWebsite(data.show_on_website !== false);
      // An older menu with no column yet reads as offered, like the server's default.
      setShowOnKiosk(data.show_on_kiosk !== false);
      // Restore the served-at branches (always keep the primary selected).
      const ids = Array.isArray(data.location_ids) ? data.location_ids.map(Number) : [];
      if (primaryLocationId && !ids.includes(primaryLocationId)) ids.push(primaryLocationId);
      setSelectedLocationIds(ids.length ? ids : (primaryLocationId ? [primaryLocationId] : []));
      setActiveFrom(data.active_from || '');
      setActiveUntil(data.active_until || '');
      setFollowLocationHours(Boolean(data.follow_location_hours));

      const loadedSections = (data.sections || []).map((section) => ({
        id: section.id,
        name: section.name,
        description: section.description || '',
        available_from: section.available_from || '',
        available_until: section.available_until || '',
        follow_location_hours: section.follow_location_hours || false,
        items: (section.items || []).map((item) => ({
          dish_id: item.dish_id,
          dish_name: item.dish_name || 'Unknown',
          dish_sku: item.dish_sku || '',
          kind: item.kind,
          dish_type: item.dish_type,
          category_id: item.category_id,
          // Variants chosen for this item. `variants` (id + name) renders the
          // chips on reload; `variant_ids` restores the checkbox selection.
          variants: item.variants || [],
          variant_ids: item.variant_ids || [],
        })),
      }));
      setSections(loadedSections);
      // The API stores the website's categories as section ids; the form ticks
      // them by name, so map one to the other off the menu just loaded.
      const publishedIds = new Set((data.website_sections || []).map(Number));
      setWebsiteSections(
        loadedSections.filter((s) => publishedIds.has(Number(s.id))).map((s) => s.name),
      );
      setWebsiteShowPrices(data.website_show_prices !== false);
      if (loadedSections.length) setActiveSectionIdx(0);
    } catch (error) {
      console.error('Error loading menu:', error);
      toast({ title: 'Error', description: 'Failed to load menu', variant: 'destructive' });
      navigate(menusBase);
    } finally {
      setLoading(false);
    }
  }, [restaurantId, menuId, toast, navigate, menusBase, primaryLocationId]);

  useEffect(() => {
    if (isEditing && restaurantId) loadMenu();
    loadLocations();
    loadMasterSections();
  }, [isEditing, restaurantId, loadMenu, loadMasterSections, loadLocations]);

  // Toggle a branch in the served-at set. The primary location can't be removed
  // (the menu is created/listed under it).
  const toggleLocation = (id) => {
    if (id === primaryLocationId) return;
    setSelectedLocationIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id],
    );
  };

  // The current cover photo of a section master, looked up by section name.
  const masterImageFor = useCallback(
    (name) =>
      masterSections.find(
        (m) => m.name.trim().toLowerCase() === (name || '').trim().toLowerCase(),
      )?.image || null,
    [masterSections],
  );

  const resetSectionImageState = (preview = null) => {
    setSectionImageFile(null);
    setSectionImagePreview(preview);
    setSectionRemoveImage(false);
    if (sectionImageInputRef.current) sectionImageInputRef.current.value = '';
  };

  const openAddSection = (presetName = '') => {
    setEditingSectionIndex(null);
    setSectionName(presetName);
    setSectionDescription('');
    setSectionFromTime('');
    setSectionUntilTime('');
    setSectionFollowHours(false);
    resetSectionImageState(masterImageFor(presetName));
    setShowSectionDialog(true);
  };

  // Reset sub-category when the parent category changes — keeps the dropdown
  // honest: if the new category has no children, "all" matches anything.
  useEffect(() => {
    setPickerSubCategory('all');
  }, [pickerCategory]);

  // Sub-categories visible under the current category filter.
  const visibleSubCategories = useMemo(() => {
    if (pickerCategory === 'all') return [];
    const catId = parseInt(pickerCategory, 10);
    if (!catId) return [];
    return subCategories.filter((s) => s.category_id === catId);
  }, [subCategories, pickerCategory]);

  // Fetch every dish ONCE (re-run only when the restaurant/location changes or a
  // new dish was just created — pickerNonce). Filtering happens client-side.
  useEffect(() => {
    let cancelled = false;
    if (!restaurantId) return undefined;
    (async () => {
      setPickerLoading(true);
      try {
        const rows = await getAllDishes(restaurantId, locationId);
        if (!cancelled) setAllDishes(rows);
      } finally {
        if (!cancelled) setPickerLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [restaurantId, locationId, pickerNonce]);

  // Client-side filter: type chip (picker_type), category, sub-category and
  // search — mirrors exactly what the server used to filter, but with no extra
  // round-trips.
  const pickerDishes = useMemo(() => {
    const catId = pickerCategory !== 'all' ? parseInt(pickerCategory, 10) : null;
    const subId = pickerSubCategory !== 'all' ? parseInt(pickerSubCategory, 10) : null;
    const q = pickerSearch.trim().toLowerCase();
    return allDishes.filter((d) => {
      if (pickerType !== 'all' && d.picker_type !== pickerType) return false;
      if (catId != null && d.category?.id !== catId) return false;
      if (subId != null && d.sub_category?.id !== subId) return false;
      if (q && !(d.name || '').toLowerCase().includes(q)) return false;
      return true;
    });
  }, [allDishes, pickerType, pickerCategory, pickerSubCategory, pickerSearch]);

  // Master sections still pickable in the dropdown — hide ones already added
  // (so you can't create duplicates), but always keep the one being edited.
  const availableMasterSections = useMemo(() => {
    const used = new Set(
      sections.map((s) => s.name.trim().toLowerCase()),
    );
    return masterSections.filter(
      (m) =>
        m.name === sectionName ||
        !used.has(m.name.trim().toLowerCase()),
    );
  }, [masterSections, sections, sectionName]);

  /* The website's categories, narrowed to sections the menu still carries —
     rename or remove a section and it simply stops being published, rather
     than leaving a name in the list that points at nothing.

     The website choices are EDITED in Settings → Website → Menus, not here —
     but the full save writes every field, so this form still loads the stored
     values and echoes them back unchanged. Dropping them from the payload
     would silently reset the site with every menu edit. */
  const publishedSectionNames = useMemo(() => {
    const live = new Set(sections.map((s) => (s.name || '').trim()));
    return websiteSections.filter((n) => live.has((n || '').trim()));
  }, [sections, websiteSections]);

  // Shape the pickable masters for the SearchableSelect controls.
  const masterSectionOptions = useMemo(() => {
    const opts = availableMasterSections.map((m) => ({
      value: m.name,
      label: m.name,
      description: m.description || '',
    }));
    // Keep a legacy/custom section name selectable when editing an older menu
    // whose section isn't (or is no longer) in the master list.
    if (sectionName && !opts.some((o) => o.value === sectionName)) {
      opts.unshift({ value: sectionName, label: sectionName, description: '' });
    }
    return opts;
  }, [availableMasterSections, sectionName]);

  // Create a new master section inline (the + beside the picker), then refresh
  // the list and auto-select it — same flow as the dish-form masters.
  const createSectionMaster = async () => {
    const name = newMasterName.trim();
    if (!name) {
      toast({ title: 'Name is required', variant: 'destructive' });
      return;
    }
    setSavingMaster(true);
    try {
      if (newMasterImageFile) {
        // The dish-masters endpoint takes multipart, so the cover photo can
        // ride along with the create.
        await dishMastersService.create('section', {
          name,
          description: newMasterDescription.trim() || '',
          image: newMasterImageFile,
        });
      } else {
        await mastersService.create('menuSections', {
          name,
          description: newMasterDescription.trim() || '',
        });
      }
      await loadMasterSections();
      setSectionName(name);
      if (newMasterDescription.trim() && !sectionDescription.trim()) {
        setSectionDescription(newMasterDescription.trim());
      }
      if (newMasterImageFile) {
        resetSectionImageState(newMasterImagePreview);
      }
      toast({ title: 'Section created' });
      setShowSectionMasterDialog(false);
      setNewMasterName('');
      setNewMasterDescription('');
      setNewMasterImageFile(null);
      setNewMasterImagePreview(null);
    } catch (err) {
      const msg = err?.response?.data?.error || err?.message || 'Could not create';
      toast({ title: 'Create failed', description: msg, variant: 'destructive' });
    } finally {
      setSavingMaster(false);
    }
  };

  // ------------------------- Section CRUD ------------------------- //
  const openEditSection = (index) => {
    const section = sections[index];
    setEditingSectionIndex(index);
    setSectionName(section.name);
    setSectionDescription(section.description);
    setSectionFromTime(section.available_from || '');
    setSectionUntilTime(section.available_until || '');
    setSectionFollowHours(section.follow_location_hours || false);
    resetSectionImageState(masterImageFor(section.name));
    setShowSectionDialog(true);
  };

  // Push the chosen cover photo to the section master (creating the master
  // first for a custom name that isn't in the list yet). No-op when the image
  // wasn't touched.
  const syncSectionImage = async (name, description) => {
    if (!sectionImageFile && !sectionRemoveImage) return;
    const master = masterSections.find(
      (m) => m.name.trim().toLowerCase() === name.trim().toLowerCase(),
    );
    if (sectionImageFile) {
      if (master) {
        await dishMastersService.update('section', master.id, { image: sectionImageFile });
      } else {
        await dishMastersService.create('section', {
          name,
          description: description || '',
          image: sectionImageFile,
        });
      }
    } else if (sectionRemoveImage && master?.image) {
      await dishMastersService.update('section', master.id, { remove_image: 'true' });
    }
    await loadMasterSections();
  };

  const saveSection = async () => {
    if (!sectionName.trim()) {
      toast({ title: 'Section name is required', variant: 'destructive' });
      return;
    }
    const dupeIdx = sections.findIndex(
      (s, idx) =>
        idx !== editingSectionIndex &&
        s.name.trim().toLowerCase() === sectionName.trim().toLowerCase(),
    );
    if (dupeIdx !== -1) {
      toast({ title: 'Duplicate section name', variant: 'destructive' });
      return;
    }
    // Availability is mandatory: either follow working hours, or set both times.
    if (!sectionFollowHours && !(sectionFromTime && sectionUntilTime)) {
      toast({
        title: 'Availability required',
        description: 'Set both Available From and Until times, or tick “Available at working hours”.',
        variant: 'destructive',
      });
      return;
    }
    try {
      await syncSectionImage(sectionName.trim(), sectionDescription.trim());
    } catch (err) {
      const msg = err?.response?.data?.error || err?.message || 'Could not save the section image';
      toast({ title: 'Image upload failed', description: msg, variant: 'destructive' });
      return;
    }
    const sectionData = {
      name: sectionName.trim(),
      description: sectionDescription.trim(),
      // Following the location's hours overrides any fixed window — clear it so
      // a stale From/Until isn't carried around behind the flag.
      available_from: sectionFollowHours ? '' : sectionFromTime || '',
      available_until: sectionFollowHours ? '' : sectionUntilTime || '',
      follow_location_hours: sectionFollowHours,
      items: editingSectionIndex !== null ? sections[editingSectionIndex].items : [],
    };
    if (editingSectionIndex !== null) {
      const next = [...sections];
      next[editingSectionIndex] = { ...next[editingSectionIndex], ...sectionData };
      setSections(next);
    } else {
      const next = [...sections, sectionData];
      setSections(next);
      setActiveSectionIdx(next.length - 1);
    }
    setShowSectionDialog(false);
  };

  const deleteSection = async (index) => {
    if (!(await confirm({ title: 'Delete section?', description: `Delete this section and all its items?`, confirmText: 'Delete section', tone: 'destructive' }))) return;
    const next = sections.filter((_, i) => i !== index);
    setSections(next);
    if (next.length === 0) setActiveSectionIdx(null);
    else if (activeSectionIdx === index) setActiveSectionIdx(0);
    else if (activeSectionIdx > index) setActiveSectionIdx(activeSectionIdx - 1);
  };

  // -------------------------- Item toggle -------------------------- //
  const activeSection = activeSectionIdx !== null ? sections[activeSectionIdx] : null;
  const activeDishIds = useMemo(
    () => new Set((activeSection?.items || []).map((it) => it.dish_id)),
    [activeSection],
  );
  // dish_id -> the selected item, so a picker row can show which of its
  // variants are currently checked.
  const activeItemByDish = useMemo(() => {
    const map = new Map();
    (activeSection?.items || []).forEach((it) => map.set(it.dish_id, it));
    return map;
  }, [activeSection]);

  // dish_id -> names of the OTHER sections (not the one being edited) that
  // already include the dish. Lets the picker flag dishes already placed
  // elsewhere in this menu so they aren't re-added by mistake.
  const dishOtherSections = useMemo(() => {
    const map = new Map();
    sections.forEach((sec, idx) => {
      if (idx === activeSectionIdx) return;
      (sec.items || []).forEach((it) => {
        const names = map.get(it.dish_id) || [];
        names.push(sec.name);
        map.set(it.dish_id, names);
      });
    });
    return map;
  }, [sections, activeSectionIdx]);

  // Add the dish to the active section, or remove it if it's already there.
  const toggleDish = (dish) => {
    if (activeSectionIdx === null) {
      toast({ title: 'Pick a section first', variant: 'destructive' });
      return;
    }
    const next = [...sections];
    const items = [...next[activeSectionIdx].items];
    const idx = items.findIndex((it) => it.dish_id === dish.id);
    if (idx >= 0) {
      items.splice(idx, 1);
    } else {
      // Default: every variant is selected. The user deselects to narrow.
      const variants = dish.variants || [];
      items.push({
        dish_id: dish.id,
        dish_name: dish.name,
        dish_sku: dish.sku,
        kind: dish.kind,
        dish_type: dish.dish_type,
        category_id: dish.category?.id,
        variants,
        variant_ids: variants.map((v) => v.id),
      });
    }
    next[activeSectionIdx] = { ...next[activeSectionIdx], items };
    setSections(next);
  };

  // Toggle a single variant on an already-selected dish. At least one variant
  // must stay checked — unchecking the last one is a no-op (remove the dish
  // instead if you don't want it at all).
  const toggleVariant = (dishId, variantId) => {
    if (activeSectionIdx === null) return;
    const next = [...sections];
    const items = next[activeSectionIdx].items.map((it) => {
      if (it.dish_id !== dishId) return it;
      const has = it.variant_ids?.includes(variantId);
      if (has && it.variant_ids.length <= 1) return it; // keep at least one
      const variant_ids = has
        ? it.variant_ids.filter((id) => id !== variantId)
        : [...(it.variant_ids || []), variantId];
      return { ...it, variant_ids };
    });
    next[activeSectionIdx] = { ...next[activeSectionIdx], items };
    setSections(next);
  };

  const removeItem = (dishId) => {
    if (activeSectionIdx === null) return;
    const next = [...sections];
    next[activeSectionIdx] = {
      ...next[activeSectionIdx],
      items: next[activeSectionIdx].items.filter((it) => it.dish_id !== dishId),
    };
    setSections(next);
  };

  // -------------------------- Save menu -------------------------- //
  // `asDraft` parks an incomplete menu: it's saved inactive and skips the
  // availability-window requirement so the builder can be finished later.
  const handleSubmit = async (e, { asDraft = false } = {}) => {
    if (e && e.preventDefault) e.preventDefault();
    if (!menuName.trim()) {
      toast({ title: 'Menu name is required', variant: 'destructive' });
      return;
    }
    if (!locationId) {
      toast({ title: 'No location selected', description: 'Open a menu from a location.', variant: 'destructive' });
      return;
    }
    // The daily availability window is required: either follow location hours,
    // or set both a from and until time. Drafts are exempt.
    if (!asDraft && !followLocationHours && !(activeFrom && activeUntil)) {
      toast({
        title: 'Availability window required',
        description: 'Set both a From and Until time, or turn on “Full day (location hours)”.',
        variant: 'destructive',
      });
      return;
    }
    // A published menu needs at least one section, and at least one dish placed
    // somewhere in it — an empty menu has nothing to order. Drafts are exempt.
    if (!asDraft) {
      if (sections.length === 0) {
        toast({
          title: 'Add a section',
          description: 'Use “Add Section” to add at least one section before creating the menu.',
          variant: 'destructive',
        });
        return;
      }
      if (!sections.some((s) => (s.items || []).length > 0)) {
        toast({
          title: 'Add at least one dish',
          description: 'Pick at least one dish in a section before creating the menu.',
          variant: 'destructive',
        });
        return;
      }
    }
    setSaving(true);
    try {
      // Always include the branch the builder was opened under in the set.
      const locationIds = Array.from(
        new Set([...(selectedLocationIds || []), primaryLocationId].filter(Boolean)),
      );
      const payload = {
        name: menuName.trim(),
        description: menuDescription.trim(),
        // A draft is always parked inactive, regardless of the status toggle.
        is_active: asDraft ? false : isActive,
        menu_type: menuType,
        show_on_website: showOnWebsite,
        show_on_kiosk: showOnKiosk,
        // Only meaningful while the menu is published; sent as names, which the
        // API resolves to sections once this save has built them.
        website_sections: showOnWebsite ? publishedSectionNames : [],
        website_show_prices: websiteShowPrices,
        location_ids: locationIds,
        // Following location hours overrides the fixed clock window.
        follow_location_hours: followLocationHours,
        active_from: followLocationHours ? null : (activeFrom || null),
        active_until: followLocationHours ? null : (activeUntil || null),
        sections: sections.map((section, idx) => ({
          name: section.name,
          description: section.description,
          display_order: idx,
          available_from: section.available_from || null,
          available_until: section.available_until || null,
          follow_location_hours: section.follow_location_hours || false,
          items: section.items.map((item, itemIdx) => ({
            dish_id: item.dish_id,
            display_order: itemIdx,
            is_available: true,
            variant_ids: item.variant_ids || [],
          })),
        })),
      };
      if (isEditing) {
        await updateMenu(restaurantId, menuId, payload);
        toast({ title: asDraft ? 'Draft saved' : 'Menu updated successfully' });
      } else {
        await createMenu(restaurantId, payload);
        toast({ title: asDraft ? 'Draft saved' : 'Menu created successfully' });
      }
      navigate(menusBase);
    } catch (error) {
      console.error('Error saving menu:', error);
      toast({
        title: 'Error',
        description: error?.error || 'Failed to save menu',
        variant: 'destructive',
      });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div className="p-6 text-center text-gray-500">Loading menu…</div>;
  }

  return (
    <div className="p-6">
      <div className="w-full">
        {/* Header */}
        <button
          type="button"
          onClick={() => navigate(menusBase)}
          className="group inline-flex items-center gap-2 mb-4 pl-1.5 pr-3 py-1.5 rounded-full border border-border bg-card text-xs font-medium uppercase tracking-wider font-mono text-muted-foreground hover:text-foreground hover:border-primary/40 hover:bg-accent transition-colors"
        >
          <ArrowLeft className="h-4 w-4 transition-transform group-hover:-translate-x-0.5" />
          Back to all menus
        </button>
        <div className="flex items-center gap-4 mb-6">
          <div className="flex-1">
            <h1 className="font-display text-3xl font-semibold text-foreground tracking-tight leading-none">
              {isEditing ? 'Edit Menu' : 'Create New Menu'}
            </h1>
          </div>
          <button
            type="button"
            onClick={() => setIsActive((p) => !p)}
            className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full border text-sm transition-colors ${
              isActive
                ? 'border-success/30 bg-success-soft text-success'
                : 'border-border bg-secondary text-muted-foreground'
            }`}
          >
            <span className={`h-2 w-2 rounded-full ${isActive ? 'bg-success' : 'bg-muted-foreground'}`} />
            {isActive ? 'Active' : 'Inactive'}
          </button>
          <div className="flex items-center gap-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => navigate(menusBase)}
            >
              Cancel
            </Button>
            <Button
              type="button"
              variant="secondary"
              disabled={saving}
              onClick={() => handleSubmit(null, { asDraft: true })}
            >
              {saving ? 'Saving…' : 'Save as Draft'}
            </Button>
            <Button type="submit" form="menu-builder-form" disabled={saving}>
              {saving ? 'Saving…' : isEditing ? 'Update Menu' : 'Create Menu'}
            </Button>
          </div>
        </div>

        <form id="menu-builder-form" onSubmit={handleSubmit} className="space-y-6">
          {/* TOP ROW — one card, three columns: details · locations · window */}
          <Card>
            <CardContent className="grid grid-cols-1 lg:grid-cols-3 gap-x-8 gap-y-6 pt-6 items-start">
              {/* Menu details */}
              <div className="space-y-3">
                <div>
                  <Label htmlFor="name">Menu Name <span className="font-bold text-foreground">*</span></Label>
                  <Input
                    id="name"
                    value={menuName}
                    onChange={(e) => setMenuName(e.target.value)}
                    placeholder="e.g., Main Menu, Weekend Special"
                    required
                  />
                </div>
                <div>
                  <Label htmlFor="description">Description</Label>
                  <Textarea
                    id="description"
                    value={menuDescription}
                    onChange={(e) => setMenuDescription(e.target.value)}
                    placeholder="Brief description of this menu"
                    rows={2}
                  />
                </div>
                {/* Service style — à la carte or buffet spread */}
                <div>
                  <Label>Menu Type</Label>
                  <div className="mt-1 grid grid-cols-2 gap-2">
                    {[['ALACARTE', 'À la carte', 'Guests order dishes individually'],
                      ['BUFFET', 'Buffet', 'Spread menu — per-head pricing via Open Buffet']].map(([v, l, hint]) => (
                      <button
                        key={v}
                        type="button"
                        onClick={() => setMenuType(v)}
                        title={hint}
                        className={`rounded-md border px-3 py-2 text-sm font-medium transition ${
                          menuType === v
                            ? 'border-primary bg-primary text-primary-foreground'
                            : 'border-slate-200 bg-white text-slate-700 hover:border-slate-300'
                        }`}
                      >
                        {l}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Whether a self-order kiosk offers this card. Its own switch
                    rather than a per-branch setting: a restaurant running a
                    long dine-in card and a short counter one decides that
                    menu by menu, and the panel should never offer a dish the
                    counter cannot make in the time a queue will wait. */}
                <div>
                  <Label>{tr('menus.kiosk.label')}</Label>
                  <label className="mt-1 flex cursor-pointer items-start gap-3 rounded-md
                                    border border-slate-200 bg-white px-3 py-2">
                    <input
                      type="checkbox"
                      className="mt-0.5 h-4 w-4 rounded border-slate-300"
                      checked={showOnKiosk}
                      onChange={(e) => setShowOnKiosk(e.target.checked)}
                    />
                    <span className="text-sm">
                      <span className="font-medium text-slate-700">{tr('menus.kiosk.show')}</span>
                      <span className="mt-0.5 block text-xs text-slate-500">
                        {tr('menus.kiosk.hint')}
                      </span>
                    </span>
                  </label>
                </div>
              </div>

              {/* Where and when, stacked into one column. Both are short
                  answers about the same thing — which branches serve this
                  menu and during what hours — and each on its own column
                  left most of the card empty next to a tall details column. */}
              <div className="space-y-6">
              {/* Served-at locations — the menu shows under every checked branch.
                  The primary (opened-under) location stays selected. */}
              <div className="space-y-3">
                <div className="flex items-center gap-1.5 text-xs uppercase tracking-wider font-mono font-semibold text-foreground">
                  <MapPin className="h-3.5 w-3.5 text-primary" />
                  Available at locations <span className="font-bold text-foreground">*</span>
                </div>
                <div className="space-y-1 max-h-56 overflow-y-auto rounded-md border border-slate-200 p-2">
                  {locations.length === 0 ? (
                    <p className="text-xs text-slate-400 py-1 px-1">No other locations available.</p>
                  ) : (
                    locations.map((loc) => {
                      const isPrimary = loc.id === primaryLocationId;
                      const checked = selectedLocationIds.includes(loc.id);
                      return (
                        <label
                          key={loc.id}
                          className={`flex items-center gap-2 rounded px-1.5 py-1 text-sm ${
                            isPrimary ? 'opacity-80 cursor-default' : 'cursor-pointer hover:bg-slate-50'
                          }`}
                        >
                          <input
                            type="checkbox"
                            className="h-4 w-4"
                            checked={checked}
                            disabled={isPrimary}
                            onChange={() => toggleLocation(loc.id)}
                          />
                          <span className="text-slate-700 truncate">{loc.name}</span>
                          {(() => {
                            const h = locationHours(loc.operating_hours);
                            return (
                              <span className="ml-auto flex items-center gap-2 shrink-0 pl-2">
                                <span
                                  className={`inline-flex items-center gap-1 text-[10px] font-medium ${
                                    loc.is_active ? 'text-emerald-600' : 'text-slate-400'
                                  }`}
                                  title={loc.is_active ? 'Location is active' : 'Location is inactive'}
                                >
                                  <span className={`h-1.5 w-1.5 rounded-full ${loc.is_active ? 'bg-emerald-500' : 'bg-slate-300'}`} />
                                  {loc.is_active ? 'Active' : 'Inactive'}
                                </span>
                                {h ? (
                                  <span className="text-[11px] font-mono text-slate-500" title={h.title}>
                                    {h.label}
                                  </span>
                                ) : (
                                  <span className="text-[11px] italic text-slate-300">Hours not set</span>
                                )}
                                {isPrimary && (
                                  <span className="text-[10px] uppercase tracking-wider text-slate-400">
                                    Primary
                                  </span>
                                )}
                              </span>
                            );
                          })()}
                        </label>
                      );
                    })
                  )}
                </div>
              </div>

              {/* Menu-level availability window — when the menu is active. */}
              <div className="space-y-3">
                <div className="flex items-center gap-1.5 text-xs uppercase tracking-wider font-mono font-semibold text-foreground">
                  <CalendarDays className="h-3.5 w-3.5 text-primary" />
                  Availability window <span className="font-bold text-foreground">*</span>
                </div>
                {/* Full day / location-hours toggle. When on, the menu runs for
                    each branch's operating hours and the time pickers are hidden. */}
                <label className="flex items-start gap-2 rounded-md border border-slate-200 bg-slate-50 px-3 py-2.5 cursor-pointer select-none">
                  <input
                    type="checkbox"
                    checked={followLocationHours}
                    onChange={(e) => setFollowLocationHours(e.target.checked)}
                    className="mt-0.5 h-4 w-4 rounded border-border text-primary focus:ring-ring"
                  />
                  <span className="text-sm text-slate-700">
                    Full day (location hours)
                  </span>
                </label>

                {!followLocationHours && (
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label className="text-xs flex items-center gap-1">
                        <Clock className="h-3 w-3" />
                        From (time) <span className="font-bold text-foreground">*</span>
                      </Label>
                      <div className="mt-1">
                        <TimePicker value={activeFrom} onChange={setActiveFrom} />
                      </div>
                    </div>
                    <div>
                      <Label className="text-xs flex items-center gap-1">
                        <Clock className="h-3 w-3" />
                        Until (time) <span className="font-bold text-foreground">*</span>
                      </Label>
                      <div className="mt-1">
                        <TimePicker value={activeUntil} onChange={setActiveUntil} />
                      </div>
                    </div>
                  </div>
                )}
              </div>
              </div>

              {/* The website choices moved to Settings → Website → Menus:
                  which menus the site prints is a marketing decision, not a
                  menu edit, and it now sits with the site's photos and
                  traffic. This card says where it went — a control that
                  vanishes without a forwarding address reads as a bug. */}
              <div className="space-y-3">
                <div className="rounded-md border border-slate-200 px-3 py-2.5">
                  <span className="flex items-center gap-1.5 text-sm font-medium text-slate-700">
                    <Globe className="h-3.5 w-3.5 text-primary" /> Public website
                  </span>
                  <p className="text-xs text-slate-500 mt-0.5">
                    {showOnWebsite
                      ? `Shown on your website${publishedSectionNames.length
                        ? ` — ${publishedSectionNames.length} ${publishedSectionNames.length === 1 ? 'category' : 'categories'}${websiteShowPrices ? ', with prices' : ', without prices'}.`
                        : ', but no categories are ticked yet.'}`
                      : 'Not shown on your website.'}
                  </p>
                  <Link
                    to="/settings/website"
                    className="mt-1.5 inline-block text-xs font-medium text-primary underline underline-offset-2 hover:opacity-80"
                  >
                    Manage in Settings → Website
                  </Link>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* MAIN ROW — sections list (left) + section content (right) */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 items-start">
            <div className="lg:col-span-1">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle>Sections</CardTitle>
                <Button type="button" size="sm" onClick={() => openAddSection()}>
                  <Plus className="h-4 w-4 mr-1" />
                  Add Section
                </Button>
              </CardHeader>
              <CardContent className="space-y-2">
                {sections.length === 0 ? (
                  <p className="text-sm text-gray-500 text-center py-2">
                    No sections yet. Use “Add Section” to pick one from the
                    master list or add a custom section.
                  </p>
                ) : (
                  sections.map((section, index) => {
                    const selected = activeSectionIdx === index;
                    return (
                      <div
                        key={index}
                        className={`rounded-md border p-3 cursor-pointer transition-colors ${
                          selected
                            ? 'border-primary bg-accent ring-1 ring-primary/30'
                            : 'border-slate-200 bg-white hover:border-slate-300'
                        }`}
                        onClick={() => setActiveSectionIdx(index)}
                      >
                        <div className="flex items-start justify-between gap-2">
                          <div className="flex-1 min-w-0">
                            <div className="font-medium text-slate-900 truncate">{section.name}</div>
                            {section.description && (
                              <div className="text-xs text-slate-500 truncate">{section.description}</div>
                            )}
                            <div className="flex items-center gap-3 mt-1 text-xs text-slate-500">
                              {section.follow_location_hours ? (
                                <span className="inline-flex items-center gap-1">
                                  <Clock className="h-3 w-3" />
                                  Working hours
                                </span>
                              ) : (section.available_from || section.available_until) && (
                                <span className="inline-flex items-center gap-1">
                                  <Clock className="h-3 w-3" />
                                  {section.available_from || '--:--'}–{section.available_until || '--:--'}
                                </span>
                              )}
                              <span>{section.items.length} item(s)</span>
                            </div>
                          </div>
                          <div className="flex gap-1 shrink-0" onClick={(e) => e.stopPropagation()}>
                            <Button type="button" variant="ghost" size="sm" onClick={() => openEditSection(index)}>
                              <Edit className="h-3.5 w-3.5" />
                            </Button>
                            <Button
                              type="button"
                              variant="ghost"
                              size="sm"
                              onClick={() => deleteSection(index)}
                              className="text-red-600 hover:text-red-700"
                            >
                              <Trash2 className="h-3.5 w-3.5" />
                            </Button>
                          </div>
                        </div>
                      </div>
                    );
                  })
                )}
              </CardContent>
            </Card>
          </div>

          {/* RIGHT — Active-section picker */}
          <div className="lg:col-span-2 space-y-6">
            <Card className="min-h-[500px]">
              <CardHeader>
                <div className="flex items-center justify-between gap-3">
                  <div>
                    <CardTitle>
                      {activeSection ? activeSection.name : 'Section content'}
                    </CardTitle>
                    {!activeSection && (
                      <CardDescription>
                        Add a section on the left to start picking dishes.
                      </CardDescription>
                    )}
                  </div>
                  <div className="flex items-center gap-3 shrink-0">
                    {activeSection && (
                      <span className="text-xs text-slate-500">
                        {activeSection.items.length} selected
                      </span>
                    )}
                    {/* Refresh — re-fetch the dish / beverage / add-on picker
                        from the server (picks up items created elsewhere). */}
                    <button
                      type="button"
                      onClick={() => setPickerNonce((n) => n + 1)}
                      disabled={pickerLoading}
                      title="Refresh dishes"
                      aria-label="Refresh dishes"
                      className="inline-flex items-center gap-1.5 rounded-md border border-slate-200 bg-white px-2.5 py-1 text-xs font-medium text-slate-600 hover:bg-slate-50 hover:text-slate-800 hover:border-slate-300 disabled:opacity-50 transition-colors"
                    >
                      <RefreshCw className={`h-3.5 w-3.5 ${pickerLoading ? 'animate-spin' : ''}`} />
                      Refresh
                    </button>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                {!activeSection ? (
                  <div className="flex flex-col items-center justify-center text-center py-20 text-slate-400">
                    <Layers className="h-10 w-10 mb-3 opacity-60" />
                    <p className="text-sm">No section selected yet.</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {/* Selected chips */}
                    {activeSection.items.length > 0 && (
                      <div className="rounded-md border border-success/30 bg-success-soft p-3">
                        <div className="text-xs font-medium text-success mb-2">
                          In this section
                        </div>
                        <div className="flex flex-wrap gap-1.5">
                          {activeSection.items.map((item) => {
                            const totalVariants = item.variants?.length || 0;
                            const chosen = item.variant_ids?.length || 0;
                            // Only call out variants when the item carries them
                            // and the user has narrowed below the full set.
                            const variantNote =
                              totalVariants > 0 && chosen < totalVariants
                                ? ` (${chosen}/${totalVariants})`
                                : '';
                            return (
                            <span
                              key={item.dish_id}
                              className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-card border border-success/40 text-xs text-foreground"
                            >
                              {item.dish_name}
                              {variantNote && (
                                <span className="text-slate-500">{variantNote}</span>
                              )}
                              <button
                                type="button"
                                onClick={() => removeItem(item.dish_id)}
                                className="ml-1 text-destructive hover:opacity-80"
                                aria-label="Remove"
                              >
                                ×
                              </button>
                            </span>
                            );
                          })}
                        </div>
                      </div>
                    )}

                    {/* Filters */}
                    <div className="flex flex-wrap gap-1.5">
                      {TYPE_CHIPS.map((opt) => {
                        const active = pickerType === opt.value;
                        return (
                          <button
                            key={opt.value}
                            type="button"
                            onClick={() => setPickerType(opt.value)}
                            className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border text-xs font-medium transition-colors ${
                              active
                                ? 'border-primary bg-accent text-accent-foreground'
                                : 'border-slate-200 bg-white text-slate-600 hover:bg-slate-50'
                            }`}
                          >
                            <opt.Icon className="h-3.5 w-3.5" />
                            {opt.label}
                          </button>
                        );
                      })}
                    </div>

                    <div className="flex flex-wrap gap-3 items-center">
                      <select
                        value={pickerCategory}
                        onChange={(e) => setPickerCategory(e.target.value)}
                        className="h-9 px-3 rounded-md border border-slate-200 bg-white text-sm"
                      >
                        <option value="all">All categories</option>
                        {categories.map((cat) => (
                          <option key={cat.id} value={cat.id}>
                            {cat.name}
                          </option>
                        ))}
                      </select>
                      <select
                        value={pickerSubCategory}
                        onChange={(e) => setPickerSubCategory(e.target.value)}
                        disabled={pickerCategory === 'all'}
                        title={
                          pickerCategory === 'all'
                            ? 'Pick a category first'
                            : undefined
                        }
                        className="h-9 px-3 rounded-md border border-slate-200 bg-white text-sm disabled:bg-slate-50 disabled:text-slate-400 disabled:cursor-not-allowed"
                      >
                        <option value="all">
                          {pickerCategory === 'all'
                            ? 'All sub-categories'
                            : visibleSubCategories.length === 0
                              ? 'No sub-categories'
                              : 'All sub-categories'}
                        </option>
                        {visibleSubCategories.map((sub) => (
                          <option key={sub.id} value={sub.id}>
                            {sub.name}
                          </option>
                        ))}
                      </select>
                      <div className="relative flex-1 min-w-[180px]">
                        <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-slate-400" />
                        <Input
                          placeholder="Search dishes…"
                          value={pickerSearch}
                          onChange={(e) => setPickerSearch(e.target.value)}
                          className="pl-8 h-9"
                        />
                      </div>
                    </div>

                    {/* Dish list */}
                    <div className="border rounded-md max-h-[480px] overflow-y-auto divide-y">
                      {pickerLoading ? (
                        <div className="p-8 text-center text-sm text-slate-500">Loading dishes…</div>
                      ) : pickerDishes.length === 0 ? (
                        <div className="p-8 text-center text-sm text-slate-500">
                          {pickerType === 'combo'
                            ? 'No combo dishes yet. Mark a dish as combo to see it here.'
                            : 'No dishes match your filters.'}
                        </div>
                      ) : (
                        pickerDishes.map((dish) => {
                          const on = activeDishIds.has(dish.id);
                          const hasVariants = dish.variants?.length > 0;
                          const selectedVariantIds = new Set(
                            activeItemByDish.get(dish.id)?.variant_ids || [],
                          );
                          // Sections (other than this one) already using the dish.
                          const usedIn = dishOtherSections.get(dish.id) || [];
                          // A dish can live in only one section per menu. If it's
                          // already placed elsewhere, lock the row so it can't be
                          // added here too (remove it from the other section first).
                          const lockedElsewhere = !on && usedIn.length > 0;
                          return (
                          <div
                            key={dish.id}
                            className={`p-3 transition ${
                              on ? 'bg-accent' : lockedElsewhere ? 'bg-slate-50 opacity-60' : 'hover:bg-slate-50'
                            }`}
                          >
                            <label
                              className={`flex items-center gap-3 ${
                                lockedElsewhere ? 'cursor-not-allowed' : 'cursor-pointer'
                              }`}
                              title={lockedElsewhere ? `Already in: ${usedIn.join(', ')}. Remove it there to move it here.` : undefined}
                            >
                              <input
                                type="checkbox"
                                checked={on}
                                disabled={lockedElsewhere}
                                onChange={() => toggleDish(dish)}
                                className="h-4 w-4 shrink-0 disabled:cursor-not-allowed"
                              />
                              <div className="flex items-center gap-2 flex-wrap min-w-0">
                                <span className="font-mono text-[10px] text-slate-500 bg-slate-100 px-1.5 py-0.5 rounded">
                                  {dish.sku}
                                </span>
                                <span className="font-medium text-slate-900">{dish.name}</span>
                                {(dish.category || dish.sub_category) && (
                                  <span className="text-xs text-slate-500">
                                    ({[dish.category?.name, dish.sub_category?.name]
                                      .filter(Boolean)
                                      .join(' · ')})
                                  </span>
                                )}
                                {usedIn.length > 0 && (
                                  <span
                                    title={`Already in: ${usedIn.join(', ')}`}
                                    className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-success-soft text-success text-[10px] font-medium"
                                  >
                                    <Check className="h-2.5 w-2.5" />
                                    In {usedIn.length === 1 ? usedIn[0] : `${usedIn.length} sections`}
                                  </span>
                                )}
                              </div>
                              <span className="ml-auto text-[10px] uppercase tracking-wider text-slate-500 shrink-0">
                                {dish.picker_type}
                              </span>
                            </label>

                            {hasVariants && (
                              on ? (
                                // Selected: choose which variants apply (at least one).
                                <div className="mt-2 ml-7 flex flex-wrap gap-1.5">
                                  {dish.variants.map((v) => {
                                    const checked = selectedVariantIds.has(v.id);
                                    return (
                                      <label
                                        key={v.id}
                                        className={`inline-flex items-center gap-1.5 px-2 py-1 rounded-full border text-[11px] cursor-pointer transition ${
                                          checked
                                            ? 'border-primary bg-card text-foreground'
                                            : 'border-slate-200 bg-slate-50 text-slate-400'
                                        }`}
                                      >
                                        <input
                                          type="checkbox"
                                          checked={checked}
                                          onChange={() => toggleVariant(dish.id, v.id)}
                                          className="h-3 w-3"
                                        />
                                        {v.variant_name}
                                        {v.is_free
                                          ? ' · Free'
                                          : v.is_seasonal
                                            ? ' · Seasonal'
                                            : v.price != null && ` · ${formatCurrency(v.price)}`}
                                      </label>
                                    );
                                  })}
                                </div>
                              ) : (
                                // Not selected: read-only preview of what's available.
                                <div className="mt-1 ml-7 flex flex-wrap gap-1">
                                  {dish.variants.map((v) => (
                                    <span
                                      key={v.id}
                                      className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-slate-100 text-[10px] text-slate-600"
                                    >
                                      {v.variant_name}
                                      {v.is_free
                                        ? ' · Free'
                                        : v.is_seasonal
                                          ? ' · Seasonal'
                                          : v.price != null && ` · ${formatCurrency(v.price)}`}
                                    </span>
                                  ))}
                                </div>
                              )
                            )}
                          </div>
                          );
                        })
                      )}
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>

            <div className="flex justify-end gap-3">
              <Button
                type="button"
                variant="outline"
                onClick={() => navigate(menusBase)}
              >
                Cancel
              </Button>
              <Button
                type="button"
                variant="secondary"
                disabled={saving}
                onClick={() => handleSubmit(null, { asDraft: true })}
              >
                {saving ? 'Saving…' : 'Save as Draft'}
              </Button>
              <Button type="submit" disabled={saving}>
                {saving ? 'Saving…' : isEditing ? 'Update Menu' : 'Create Menu'}
              </Button>
            </div>
          </div>
          </div>
        </form>
      </div>

      {/* Section dialog (metadata only — items are managed in the right panel) */}
      <Dialog open={showSectionDialog} onOpenChange={setShowSectionDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {editingSectionIndex !== null ? 'Edit Section' : 'Add Section'}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <div className="flex items-center gap-2 mb-1">
                <Label htmlFor="section-name">Section Name <span className="font-bold text-foreground">*</span></Label>
                <IconBtn
                  icon={Plus}
                  onClick={() => {
                    setNewMasterName('');
                    setNewMasterDescription('');
                    setNewMasterImageFile(null);
                    setNewMasterImagePreview(null);
                    if (newMasterImageInputRef.current) newMasterImageInputRef.current.value = '';
                    setShowSectionMasterDialog(true);
                  }}
                  title="Add new section" />
                <IconBtn
                  icon={RefreshCw}
                  tone="subdued"
                  onClick={loadMasterSections}
                  title="Refresh sections" />
              </div>
              <SearchableSelect
                id="section-name"
                options={masterSectionOptions}
                value={sectionName}
                onChange={(name) => {
                  setSectionName(name);
                  // Prefill the description from the master if blank.
                  const m = masterSections.find((x) => x.name === name);
                  if (m && !sectionDescription.trim()) {
                    setSectionDescription(m.description || '');
                  }
                  // Show the picked master's current cover photo — unless the
                  // user already chose a new file in this dialog.
                  if (!sectionImageFile) {
                    resetSectionImageState(m?.image || null);
                  }
                }}
                placeholder={masterSectionsLoading ? 'Loading sections…' : 'Select a section…'}
                searchPlaceholder="Search sections…"
                emptyText="No sections match"
                loading={masterSectionsLoading}
              />
              {masterSections.length === 0 && !masterSectionsLoading && (
                <p className="text-xs text-slate-400 mt-1">
                  No sections yet — use the + to create one.
                </p>
              )}
            </div>
            <div>
              <Label htmlFor="section-desc">Description</Label>
              <Textarea
                id="section-desc"
                value={sectionDescription}
                onChange={(e) => setSectionDescription(e.target.value)}
                placeholder="Optional description"
                rows={2}
              />
            </div>
            {/* Cover photo — shown on the customer menu's category tiles.
                Stored on the section master, so it's shared across menus. */}
            <div>
              <Label>Cover Photo</Label>
              <div className="mt-1 flex items-center gap-3">
                <button
                  type="button"
                  onClick={() => sectionImageInputRef.current?.click()}
                  aria-label={sectionImagePreview ? 'Change cover photo' : 'Upload cover photo'}
                  className="relative h-20 w-24 shrink-0 rounded-md border border-dashed border-slate-300 bg-slate-50 overflow-hidden flex items-center justify-center text-slate-400 hover:border-primary/50 hover:text-slate-500 transition-colors"
                >
                  {sectionImagePreview ? (
                    <img src={sectionImagePreview} alt="Section cover" className="h-full w-full object-cover" />
                  ) : (
                    <ImagePlus className="h-6 w-6" />
                  )}
                </button>
                <div className="min-w-0 space-y-1">
                  <div className="flex items-center gap-2">
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => sectionImageInputRef.current?.click()}
                    >
                      {sectionImagePreview ? 'Change Photo' : 'Choose Photo'}
                    </Button>
                    {sectionImagePreview && (
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        className="text-red-600 hover:text-red-700"
                        onClick={() => {
                          resetSectionImageState(null);
                          setSectionRemoveImage(true);
                        }}
                      >
                        <X className="h-3.5 w-3.5 mr-1" />
                        Remove
                      </Button>
                    )}
                  </div>
                  <p className="text-[11px] text-slate-500">
                    Shown on the customer menu tile. Shared wherever this
                    section is used; without one the first dish photo is used.
                  </p>
                </div>
                <input
                  ref={sectionImageInputRef}
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={(e) => {
                    const f = e.target.files?.[0];
                    if (!f) return;
                    setSectionImageFile(f);
                    setSectionImagePreview(URL.createObjectURL(f));
                    setSectionRemoveImage(false);
                  }}
                />
              </div>
            </div>
            <label className="flex items-start gap-2 rounded-md border border-slate-200 bg-slate-50 px-3 py-2.5 cursor-pointer select-none">
              <input
                type="checkbox"
                checked={sectionFollowHours}
                onChange={(e) => setSectionFollowHours(e.target.checked)}
                className="mt-0.5 h-4 w-4 rounded border-border text-primary focus:ring-ring"
              />
              <span className="text-sm text-slate-700">
                Available at working hours <span className="font-bold text-foreground">*</span>
              </span>
            </label>

            {!sectionFollowHours && (
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="from-time">Available From <span className="font-bold text-foreground">*</span></Label>
                  <div className="mt-1">
                    <TimePicker
                      id="from-time"
                      value={sectionFromTime}
                      onChange={setSectionFromTime}
                    />
                  </div>
                </div>
                <div>
                  <Label htmlFor="until-time">Available Until <span className="font-bold text-foreground">*</span></Label>
                  <div className="mt-1">
                    <TimePicker
                      id="until-time"
                      value={sectionUntilTime}
                      onChange={setSectionUntilTime}
                    />
                  </div>
                </div>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowSectionDialog(false)}>Cancel</Button>
            <Button onClick={saveSection}>Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Inline create-a-section-master dialog (the + beside the picker) */}
      <Dialog open={showSectionMasterDialog} onOpenChange={setShowSectionMasterDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add Section</DialogTitle>
          </DialogHeader>
          <form
            onSubmit={(e) => {
              e.preventDefault();
              createSectionMaster();
            }}
            className="space-y-4"
          >
            <div className="space-y-1.5">
              <Label htmlFor="new-master-name">Name <span className="font-bold text-foreground">*</span></Label>
              <Input
                id="new-master-name"
                autoFocus
                value={newMasterName}
                onChange={(e) => setNewMasterName(e.target.value)}
                placeholder="e.g., Lunch, Dinner, Breakfast"
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="new-master-desc">Description</Label>
              <Textarea
                id="new-master-desc"
                rows={2}
                value={newMasterDescription}
                onChange={(e) => setNewMasterDescription(e.target.value)}
                placeholder="Optional"
              />
            </div>
            {/* Cover photo — same master image shown on the customer menu tiles. */}
            <div className="space-y-1.5">
              <Label>Cover Photo</Label>
              <div className="flex items-center gap-3">
                <button
                  type="button"
                  onClick={() => newMasterImageInputRef.current?.click()}
                  aria-label={newMasterImagePreview ? 'Change cover photo' : 'Upload cover photo'}
                  className="relative h-20 w-24 shrink-0 rounded-md border border-dashed border-slate-300 bg-slate-50 overflow-hidden flex items-center justify-center text-slate-400 hover:border-primary/50 hover:text-slate-500 transition-colors"
                >
                  {newMasterImagePreview ? (
                    <img src={newMasterImagePreview} alt="Section cover" className="h-full w-full object-cover" />
                  ) : (
                    <ImagePlus className="h-6 w-6" />
                  )}
                </button>
                <div className="min-w-0 space-y-1">
                  <div className="flex items-center gap-2">
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => newMasterImageInputRef.current?.click()}
                    >
                      {newMasterImagePreview ? 'Change Photo' : 'Choose Photo'}
                    </Button>
                    {newMasterImagePreview && (
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        className="text-red-600 hover:text-red-700"
                        onClick={() => {
                          setNewMasterImageFile(null);
                          setNewMasterImagePreview(null);
                          if (newMasterImageInputRef.current) newMasterImageInputRef.current.value = '';
                        }}
                      >
                        <X className="h-3.5 w-3.5 mr-1" />
                        Remove
                      </Button>
                    )}
                  </div>
                  <p className="text-[11px] text-slate-500">
                    Shown on the customer menu tile. Optional — you can also add
                    it later when editing the section.
                  </p>
                </div>
                <input
                  ref={newMasterImageInputRef}
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={(e) => {
                    const f = e.target.files?.[0];
                    if (!f) return;
                    setNewMasterImageFile(f);
                    setNewMasterImagePreview(URL.createObjectURL(f));
                  }}
                />
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowSectionMasterDialog(false)} disabled={savingMaster}>
                Cancel
              </Button>
              <Button type="submit" disabled={savingMaster}>
                {savingMaster ? 'Creating…' : 'Create Section'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
